import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ListChecks, Users, CalendarClock, MessageSquareText, Mail, FolderOpen, BookOpen, Star,
  ChevronDown, LogOut, PanelLeftClose, PanelLeftOpen, Menu, Clock,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { useMe, useUpdateMyTimezone } from "../../lib/rehearsalRoomQueries";
import { rrLogout } from "../../lib/rehearsalRoomAuth";
import { toast } from "sonner";
import { GlobalSearch } from "./coach/GlobalSearch";
import { NewMenu } from "./coach/NewMenu";
import { NotificationsBell } from "./coach/NotificationsBell";
import { SaoPauloClock } from "./coach/SaoPauloClock";

// Master v5 Prompt 31.1 — "Client Timezone on File" applies to a coach
// too, not just a client — see ClientShell.jsx's own TIMEZONE_OPTIONS
// for why Intl.supportedValuesOf is preferred over a hardcoded list.
const TIMEZONE_OPTIONS = (() => {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return [Intl.DateTimeFormat().resolvedOptions().timeZone];
  }
})();

const NAV_ITEMS = [
  { key: "today", label: "Today", icon: ListChecks },
  { key: "clients", label: "Clients", icon: Users },
  { key: "sessions", label: "Sessions", icon: CalendarClock },
  { key: "feedback", label: "Feedback", icon: MessageSquareText },
  { key: "messages", label: "Messages", icon: Mail },
  { key: "files", label: "Files", icon: FolderOpen },
  { key: "library", label: "Library", icon: BookOpen },
  { key: "reviews", label: "Reviews", icon: Star },
];

const SIDEBAR_COLLAPSE_KEY = "rr_coach_sidebar_collapsed";

/**
 * Master v5 Prompt 16 — Coach operating shell. Cross-Workspace by
 * design (unlike ClientShell, which is scoped to one Workspace) — the
 * coach's home surface is Coach Today, not any single client's Home.
 * Sidebar destinations beyond Today/Clients open a real, functional
 * page ONLY for what Prompt 16 itself builds; Sessions/Feedback/
 * Messages/Files/Library route to an honest "not yet its own
 * cross-Workspace workbench" placeholder (Prompts 17-19/20) that still
 * points the coach at the real per-client pages via Clients.
 */
export default function CoachShell({ children }) {
  const navigate = useNavigate();
  const currentPath = useLocation().pathname;
  const { data: me } = useMe();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === "1");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  function toggleCollapsed() {
    setCollapsed((c) => {
      localStorage.setItem(SIDEBAR_COLLAPSE_KEY, !c ? "1" : "0");
      return !c;
    });
  }

  async function handleLogout() {
    await rrLogout();
    navigate("/rehearsal-room/login", { replace: true });
  }

  function navItems(onClick) {
    return NAV_ITEMS.map((item) => {
      const route = `/rehearsal-room/coach/${item.key}`;
      const active = currentPath === route;
      return (
        <button
          key={item.key}
          onClick={() => { navigate(route); onClick?.(); }}
          data-testid={`coach-shell-nav-${item.key}`}
          title={item.label}
          aria-current={active ? "page" : undefined}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors focus-visible:outline-2 focus-visible:outline-mustard ${
            active ? "bg-paper/15 text-paper" : "text-paper/70 hover:bg-paper/10 hover:text-paper"
          }`}
        >
          <item.icon size={18} className="shrink-0" />
          {!collapsed && <span className="truncate flex-1 text-left">{item.label}</span>}
        </button>
      );
    });
  }

  return (
    <div className="min-h-screen bg-paper flex" data-testid="coach-shell">
      <aside
        className={`hidden md:flex flex-col bg-ink text-paper shrink-0 transition-[width] duration-200 ${collapsed ? "w-[76px]" : "w-64"}`}
        data-testid="coach-shell-sidebar"
      >
        <div className="flex items-center justify-between px-4 py-5">
          {!collapsed && <span className="font-serif text-lg tracking-wide">InterAct Coach</span>}
          <button onClick={toggleCollapsed} className="text-paper/60 hover:text-paper transition-colors" data-testid="coach-shell-sidebar-toggle" aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
            {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>
        </div>
        <nav className="flex-1 px-2 space-y-1 mt-2" data-testid="coach-shell-sidebar-nav">{navItems()}</nav>
      </aside>

      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="bg-ink text-paper w-64 p-0" data-testid="coach-shell-mobile-nav">
          <SheetHeader className="px-4 py-5">
            <SheetTitle className="font-serif text-lg text-paper tracking-wide">InterAct Coach</SheetTitle>
          </SheetHeader>
          <nav className="px-2 space-y-1">{navItems(() => setMobileNavOpen(false))}</nav>
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col min-w-0">
        <header
          className="flex items-center justify-between gap-2 px-3 sm:px-6 py-3 border-b border-ink/10 bg-paper/90 backdrop-blur-sm sticky top-0 z-30"
          data-testid="coach-shell-topbar"
        >
          <button
            onClick={() => setMobileNavOpen(true)}
            className="md:hidden p-2 rounded-full hover:bg-ink/5 text-ink-soft hover:text-ink transition-colors shrink-0"
            aria-label="Open navigation"
            data-testid="coach-shell-mobile-nav-trigger"
          >
            <Menu size={18} />
          </button>
          <div className="flex-1 min-w-0 max-w-md flex items-center">
            <GlobalSearch />
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <NewMenu />
            <NotificationsBell />
            <SaoPauloClock timezone={me?.timezone} />
            <ProfileMenu me={me} onLogout={handleLogout} />
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 py-6">{children}</main>
      </div>
    </div>
  );
}

function ProfileMenu({ me, onLogout }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [tzOpen, setTzOpen] = useState(false);
  const [tzChoice, setTzChoice] = useState(me?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
  const updateTimezone = useUpdateMyTimezone();

  // Same Radix DropdownMenu-then-Dialog pointer-events race as
  // ClientShell.jsx — close the menu first, open the Dialog next tick.
  function openAfterMenuCloses(setOpen) {
    setMenuOpen(false);
    setTimeout(() => setOpen(true), 120);
  }

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-ink/5 transition-colors focus-visible:outline-2 focus-visible:outline-terracotta" data-testid="coach-shell-profile-trigger">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-terracotta text-paper text-xs font-medium">{(me?.name || "?").slice(0, 1).toUpperCase()}</AvatarFallback>
            </Avatar>
            <ChevronDown size={14} className="text-ink-soft" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56" data-testid="coach-shell-profile-menu">
          <DropdownMenuLabel className="text-ink-soft text-xs font-normal truncate">{me?.name || "Coach account"}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setTzChoice(me?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone); openAfterMenuCloses(setTzOpen); }} data-testid="coach-shell-menu-timezone">
            <Clock size={15} className="mr-2" /> Timezone
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onLogout} data-testid="coach-shell-menu-sign-out">
            <LogOut size={15} className="mr-2" /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={tzOpen} onOpenChange={setTzOpen}>
        <DialogContent data-testid="coach-shell-timezone-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-ink">Timezone</DialogTitle>
            <DialogDescription>
              Set your own timezone once — every Session time and confirmation email will show it
              consistently, instead of guessing from your browser each time.
            </DialogDescription>
          </DialogHeader>
          <select
            value={tzChoice}
            onChange={(e) => setTzChoice(e.target.value)}
            className="border border-input rounded-md h-9 px-2 text-sm bg-background w-full"
            data-testid="coach-shell-timezone-select"
          >
            {TIMEZONE_OPTIONS.map((tz) => <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>)}
          </select>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={async () => {
                try {
                  await updateTimezone.mutateAsync(tzChoice);
                  toast.success("Timezone updated");
                  setTzOpen(false);
                } catch {
                  toast.error("Couldn't update your timezone");
                }
              }}
              disabled={updateTimezone.isPending}
              className="bg-terracotta text-paper text-sm font-medium px-4 py-2 rounded-md hover:bg-terracotta/90 disabled:opacity-50"
              data-testid="coach-shell-timezone-save-btn"
            >
              {updateTimezone.isPending ? "Saving…" : "Save"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
